import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ORGANIZATIONS_SEARCH_DEBOUNCE_MS } from '../../../../constants/organization';
import { DashboardQueryKey } from '../../../../constants/dashboardQueryKeys';
import { useOrganizationsQuery } from '../../../../hooks/useDashboardQueries';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { useListPage } from '../../../../hooks/useListPage';

interface OrganizationsFilterState {
  search: string;
}

const INITIAL_FILTERS: OrganizationsFilterState = { search: '' };

/**
 * Server state for the organizations list. Pagination and search run
 * server-side: the debounced search term and the useListPage
 * page/rowsPerPage state feed the query key. Create/edit live on their own
 * pages (OrganizationEditor).
 */
export function useOrganizationsPage() {
  const { t } = useTranslation();

  const {
    page: pageIndex,
    rowsPerPage,
    filters,
    setPage,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
  } = useListPage<OrganizationsFilterState>({
    initialFilters: INITIAL_FILTERS,
    queryKeyRoot: DashboardQueryKey.ORGANIZATIONS,
    urlSync: true,
  });

  const search = filters.search;
  // Filter changes already reset the page to 0 (useListPage convention).
  const debouncedSearch = useDebouncedValue(search.trim(), ORGANIZATIONS_SEARCH_DEBOUNCE_MS);

  const { data, isLoading, isFetching, isPlaceholderData, error, refetch } =
    useOrganizationsQuery(pageIndex + 1, rowsPerPage, debouncedSearch);
  const organizations = data?.data ?? [];
  const total = data?.total ?? 0;

  // Clamp out-of-range pages (e.g. a stale ?page= in the URL): once fresh data confirms the page is empty, step back one page.
  useEffect(() => {
    const pageConfirmedEmpty =
      !isFetching && !isPlaceholderData && data !== undefined && data.data.length === 0;
    if (pageConfirmedEmpty && pageIndex > 0) {
      setPage(pageIndex - 1);
    }
  }, [data, isFetching, isPlaceholderData, pageIndex, setPage]);

  const fallbackMessage = t('dashboard.organizations.errorLoading');
  const listErrorMessage =
    error instanceof Error ? error.message : fallbackMessage;

  return {
    organizations,
    total,
    search,
    setSearch: (value: string) => handleFilterChange({ search: value }),
    pageIndex,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    showLoading: isLoading,
    listError: error,
    listErrorMessage,
    refetch,
  };
}
